import { computed } from "@preact/signals";
import {
  formatMoney,
  cents,
  lineRemainingQty,
  orderRemainingCents,
  type OrderLine,
} from "@orderman/shared";
import { myOrders, draft, reopenOrder, type OrderRecord } from "../state/store.js";
import { payableOf } from "../state/draft.js";
import { pendingOrderIds } from "../state/sync.js";

// Nur tatsächlich begonnene Bons (mit Zeilen oder schon gesendet).
const bons = computed(() =>
  myOrders.value.filter((r) => r.draft.sentOnce || r.draft.lines.length > 0),
);

function perUnit(l: OrderLine): number {
  return l.unitPriceCents + l.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0);
}

function time(ms: number): string {
  return new Date(ms).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });
}

interface EntryLine {
  id: string;
  name: string;
  qty: number;
  cents: number;
}

interface Entry {
  key: string;
  rec: OrderRecord;
  belegNo?: number;
  seqNo?: number;
  table: string;
  atMs: number;
  label: string;
  cls: string;
  method?: string;
  lines: EntryLine[];
  totalCents: number;
  /** Offener Rest (editierbar) → "weiter bearbeiten"; sonst abgeschlossener Beleg. */
  open: boolean;
}

/**
 * Eine Bestellung in Einträge zerlegen: je (Teil-)Zahlung ein eigener Beleg —
 * an einem Tisch zahlen oft mehrere Parteien getrennt — plus ein Eintrag für den
 * noch offenen Rest.
 */
function entriesOf(rec: OrderRecord): Entry[] {
  const pay = payableOf(rec.draft);
  const lineById = new Map(rec.draft.lines.map((dl) => [dl.line.id, dl.line]));
  const out: Entry[] = [];

  for (const p of rec.draft.payments) {
    const lines = p.items
      .map((it): EntryLine | null => {
        const l = lineById.get(it.lineId);
        return l ? { id: it.lineId, name: l.name, qty: it.qty, cents: perUnit(l) * it.qty } : null;
      })
      .filter((x): x is EntryLine => x !== null);
    out.push({
      key: `${rec.id}:${p.paymentId}`,
      rec,
      ...(p.belegNo !== undefined ? { belegNo: p.belegNo } : {}),
      ...(rec.seqNo !== undefined ? { seqNo: rec.seqNo } : {}),
      table: rec.draft.tableRef,
      atMs: p.atMs,
      label: "bezahlt",
      cls: "st-paid",
      method: p.method === "cash" ? "Bar" : "Karte",
      lines,
      totalCents: p.amountCents,
      open: false,
    });
  }

  const openLines = rec.draft.lines
    .filter((dl) => dl.line.status !== "voided")
    .map((dl) => ({ line: dl.line, rem: lineRemainingQty(pay, dl.line) }))
    .filter((x) => x.rem > 0)
    .map(
      (x): EntryLine => ({ id: x.line.id, name: x.line.name, qty: x.rem, cents: perUnit(x.line) * x.rem }),
    );
  if (openLines.length > 0) {
    const partial = rec.draft.payments.length > 0;
    out.push({
      key: `${rec.id}:open`,
      rec,
      ...(rec.seqNo !== undefined ? { seqNo: rec.seqNo } : {}),
      table: rec.draft.tableRef,
      atMs: rec.openedAtMs,
      label: partial ? "Rest offen" : rec.draft.sentOnce ? "übermittelt" : "Entwurf",
      cls: partial ? "st-partial" : rec.draft.sentOnce ? "st-sent" : "st-draft",
      lines: openLines,
      totalCents: orderRemainingCents(pay),
      open: true,
    });
  }

  return out;
}

export function MyBons() {
  const entries = bons.value.flatMap(entriesOf);
  if (entries.length === 0) {
    return <div class="empty">Noch keine Bestellungen. Nimm über „Bestellen" eine auf.</div>;
  }
  return (
    <div class="mybons">
      {entries.map((e) => {
        const isOpen = e.open && draft.value?.orderId === e.rec.id;
        return (
          <div class="bon-card" key={e.key}>
            <div class="bon-head">
              {e.belegNo !== undefined ? (
                <span class="bon-no">Beleg #{e.belegNo}</span>
              ) : e.seqNo !== undefined ? (
                <span class="bon-no">#{e.seqNo}</span>
              ) : null}
              <span class="bon-table">{e.table}</span>
              <span class="bon-time">{time(e.atMs)}</span>
              <span class={`bon-status ${e.cls}`}>{e.label}</span>
              {pendingOrderIds.value.has(e.rec.id) && (
                <span class="bon-status st-pending" title="noch nicht an den Server übermittelt">
                  ↑ nicht übermittelt
                </span>
              )}
            </div>
            <ul class="bon-lines">
              {e.lines.map((l) => (
                <li key={l.id}>
                  <span class="bl-qty">{l.qty}×</span>
                  <span class="bl-name">{l.name}</span>
                  <span class="bl-price">{formatMoney(cents(l.cents))}</span>
                </li>
              ))}
            </ul>
            <div class="bon-foot">
              <span class="bon-total">
                {formatMoney(cents(e.totalCents))}
                {e.method ? ` · ${e.method}` : ""}
              </span>
              {e.open ? (
                <button class="reopen" onClick={() => reopenOrder(e.rec)}>
                  {isOpen ? "weiter bearbeiten" : "Tisch erneut öffnen"}
                </button>
              ) : (
                <span class="closed-tag">✓ Beleg</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
